/**
 * Wave 3 — the Reassign / Delegate member picker (CALENDAR_DIARY_DESIGN §3.4).
 *
 * One dialog, two modes. The design's exact words ride along so agents stop
 * confusing the two: "Reassign moves it; Delegate lends it."
 *
 * - Reassign: ownership transfers; the original owner becomes an optional
 *   attendee (POST /:id/reassign, service-owned semantics).
 * - Delegate: the owner keeps the event; a proxy is attached who can act on
 *   the owner's behalf (POST /:id/delegate).
 */
import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { api } from '@/api/client'
import type { CalendarEvent } from '@/api/calendar'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { t, type DashboardLocale } from '@/pages/agent/dashboard/copy'

export type MemberActionMode = 'reassign' | 'delegate'

interface AgentLike {
  id: string
  user_id?: string | null
  name?: string | null
  email?: string | null
}

function errorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message
  return String(err)
}

export function MemberActionDialog({
  event,
  mode,
  targetId,
  locale,
  onClose,
  onApplied,
}: {
  event: CalendarEvent | null
  mode: MemberActionMode
  /**
   * Wave 6b — for a recurrence instance the POST must target the series id
   * (a derived occurrence id 404s). Defaults to event.id.
   */
  targetId?: string
  locale: DashboardLocale
  onClose: () => void
  onApplied: (event: CalendarEvent) => void
}) {
  const [members, setMembers] = useState<AgentLike[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!event) return
    setMembers(null)
    setFailed(false)
    setSelected(null)
    setError(null)
    let cancelled = false
    api
      .getAgents()
      .then((rows) => {
        if (cancelled) return
        setMembers(Array.isArray(rows) ? rows : [])
      })
      .catch(() => {
        if (cancelled) return
        setFailed(true)
        setMembers([])
      })
    return () => {
      cancelled = true
    }
  }, [event])

  if (!event) return null

  const submit = async () => {
    if (!selected || busy) return
    setBusy(true)
    setError(null)
    try {
      const { event: updated } = mode === 'reassign'
        ? await api.reassignCalendarEvent(targetId ?? event.id, selected)
        : await api.delegateCalendarEvent(targetId ?? event.id, selected)
      onApplied(updated)
      onClose()
    } catch (err) {
      // Booking-rule denials and plain failures both surface inline.
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t(`calendar.memberAction.${mode}.title` as const, locale)}
          </DialogTitle>
        </DialogHeader>
        <p className="text-[var(--lc-text-secondary)]" style={{ font: 'var(--lc-type-body-sm)' }}>
          {t(`calendar.memberAction.${mode}.helper` as const, locale)}
        </p>
        <div data-testid="member-picker" className="max-h-64 space-y-1 overflow-y-auto">
          {members === null && !failed ? (
            <p className="flex items-center gap-2 text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-body-sm)' }}>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            </p>
          ) : members === null || members.length === 0 ? (
            <p className="text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-body-sm)' }}>
              {t('calendar.memberAction.empty', locale)}
            </p>
          ) : (
            members.map((member) => {
              const value = member.user_id ?? member.id
              return (
                <label
                  key={member.id}
                  className="flex min-h-tap cursor-pointer items-center gap-[var(--lc-space-sm)] rounded-[var(--lc-radius-sm)] px-2 hover:bg-[var(--lc-surface-sunken)]"
                >
                  <input
                    type="radio"
                    name={`member-action-${event.id}`}
                    checked={selected === value}
                    onChange={() => setSelected(value)}
                  />
                  <span style={{ font: 'var(--lc-type-body-sm)' }}>
                    {member.name || member.email || member.id}
                  </span>
                </label>
              )
            })
          )}
        </div>
        {error ? (
          <p data-testid="member-action-error" className="text-[var(--lc-status-unpublished-fg)]" style={{ font: 'var(--lc-type-caption)' }}>
            {error}
          </p>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            {t('calendar.action.cancel', locale)}
          </Button>
          <Button type="button" onClick={() => void submit()} disabled={!selected || busy}>
            {busy ? <Loader2 className="me-1 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            {t('calendar.memberAction.confirm', locale)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
